// Works out which heaters to add to and remove from an SSR.
//
// This used to live inline in AdminSSRItemDetails.handleApply, where the
// "desired" side was read off elementSpec — an array three useEffects rewrite
// as the user types. Typing in one box blanked the others, and the diff turned
// that into DELETE calls for heaters nobody touched. Keeping the diff pure and
// fed only by the inputs the user actually edits is what makes that impossible.
//
// The diff is over multisets, not sets. An SSR is filled to a current budget
// rather than a heater count — four 5A heaters on a 20A SSR — so the same part
// number can legitimately repeat, and quantity is the thing being compared.
// Collapsing to distinct values made a second copy indistinguishable from the
// first and silently dropped both the add and the delete.
const normalize = (list) =>
  (Array.isArray(list) ? list : [])
    .map((partNumber) =>
      String(partNumber ?? '')
        .trim()
        .toUpperCase()
    )
    .filter((partNumber) => partNumber !== '');

const countBy = (list) =>
  list.reduce(
    (counts, partNumber) =>
      counts.set(partNumber, (counts.get(partNumber) ?? 0) + 1),
    new Map()
  );

// Distinct values in first-appearance order, so the output keeps the order the
// device reported (deletes) or the admin filled the boxes in (adds).
const distinct = (list) => [...new Set(list)];

const repeat = (partNumber, times) =>
  Array.from({ length: Math.max(0, times) }, () => partNumber);

/**
 * @param {string[]} currentPartNumbers  what the SSR holds now (live specs)
 * @param {string[]} desiredPartNumbers  what the form's inputs say it should hold
 * @param {{ visibleCount?: number }} [options]
 *        visibleCount caps how many of the current heaters the form is able to
 *        represent. Anything past that is invisible to the user, so its absence
 *        from desiredPartNumbers is not a request to delete it.
 * @returns {{ toAdd: string[], toDelete: string[] }}
 *        Both lists carry one entry per copy: removing two of three identical
 *        heaters yields that part number twice, and the caller issues one
 *        request per entry.
 */
export const computeHeaterChanges = (
  currentPartNumbers,
  desiredPartNumbers,
  { visibleCount } = {}
) => {
  const current = normalize(currentPartNumbers);
  const desired = normalize(desiredPartNumbers);

  const deletable =
    visibleCount === undefined ? current : current.slice(0, visibleCount);

  const currentCounts = countBy(current);
  const desiredCounts = countBy(desired);
  const deletableCounts = countBy(deletable);

  return {
    // Adds weigh against every copy on the device, including ones the form
    // cannot show — those already exist and must not be duplicated.
    toAdd: distinct(desired).flatMap((partNumber) =>
      repeat(
        partNumber,
        desiredCounts.get(partNumber) - (currentCounts.get(partNumber) ?? 0)
      )
    ),
    // Deletes weigh only against the copies the form displayed, so an
    // off-screen heater is never taken as an omission.
    toDelete: distinct(deletable).flatMap((partNumber) =>
      repeat(
        partNumber,
        deletableCounts.get(partNumber) - (desiredCounts.get(partNumber) ?? 0)
      )
    ),
  };
};

/**
 * Rebuilds an SSR's spec list after deletions have been written, removing one
 * spec per entry in toDelete rather than every spec that shares a part number.
 *
 * @param {object[]} specs     the SSR's heaters before the write
 * @param {string[]} toDelete  part numbers, one entry per deleted copy
 * @returns {object[]} the surviving specs, in their original order
 */
export const removeDeletedSpecs = (specs, toDelete) => {
  const remaining = countBy(normalize(toDelete));

  return (Array.isArray(specs) ? specs : [])
    .filter((spec) => spec?.partNumber)
    .filter((spec) => {
      const partNumber = String(spec.partNumber).trim().toUpperCase();
      const outstanding = remaining.get(partNumber) ?? 0;
      if (outstanding === 0) {
        return true;
      }
      remaining.set(partNumber, outstanding - 1);
      return false;
    });
};
